import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NEW_API from "../../Api/NewApi";
import TabsNavbar from "../Common/TabsNavBar/TabsNavbar";
import TextInput from "../Common/FormElements/TextInput/TextInput";
import TextArea from "../Common/FormElements/TextArea/TextArea";
import Select from "../Common/FormElements/Select/Select";
import Button from "../Common/FormElements/Button/Button";
import { AUTO_SOURCES, prospectSourceOptions } from "./prospectSources";
import Location from "../Prospects/NewProspect/Location/Location";
import { closeRightModal } from "../../REDUX/actions/rightModal";
import {
  createAppCustomer,
  updateAppCustomer,
} from "../../REDUX/actions/appCustomers";
import { usePartnerOrganizations } from "./usePartnerOrganizations";
import {
  getCountries,
  getCountriesCitiesNew,
} from "../../REDUX/actions/country";

const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Tab labels are raw i18n keys — TabsNavbar translates internally.
const TABS = ["General", "External note", "Location"];

// General-tab schema. Address fields edit the customer's address record (the
// same one the mobile app reads); city/country are relation ids server-side.
const formElements = [
  { type: "text", label: "Last name", name: "lastName", required: true },
  { type: "text", label: "First name", name: "firstName", required: true },
  { type: "email", label: "Email", name: "email", required: true },
  { type: "tel", label: "Phone number", name: "phoneNumber", required: true },
  { type: "text", label: "Street", name: "street" },
  { type: "text", label: "Postal code", name: "zipcode" },
  { type: "select", label: "Country", name: "country" },
  { type: "select", label: "City", name: "city" },
  { type: "select", label: "Contact", name: "contact" },
  { type: "select", label: "Role", name: "role", required: true },
];

const toOption = (entity) =>
  entity?.id ? { name: entity.name, code: entity.id } : null;

const AppCustomerModal = ({ customer, defaultRole = "Prospect" }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { createLoading, updateLoading } = useSelector(
    (state) => state.appCustomersReducer,
  );
  const { scope } = useSelector((state) => state.appCustomersReducer);
  const { countries, cities } = useSelector((state) => state.countryReducer);
  // Resellers are scoped server-side to their own organization — an admin
  // picks any store, a business affiliate one of the stores it sponsors.
  const { isAffiliate, canPickOrganization, organizationOptions } =
    usePartnerOrganizations();
  const isEdit = !!customer;

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [formData, setFormData] = useState({
    firstName: customer?.firstName ?? "",
    lastName: customer?.lastName ?? "",
    email: customer?.email ?? "",
    phoneNumber: customer?.phoneNumber ?? "",
    street: customer?.address?.street ?? "",
    zipcode: customer?.zipcode ?? customer?.address?.zip ?? "",
    country: toOption(customer?.address?.country),
    city: toOption(customer?.address?.city),
    contact: customer?.contact
      ? { name: customer.contact, code: customer.contact }
      : null,
    externalNote: customer?.externalNote ?? "",
    markers: customer?.markers ?? [],
    // New customer: the store picked in the Espace clients filter bar.
    organization: customer
      ? toOption(customer?.organization)
      : scope.organizationId
        ? { code: scope.organizationId }
        : null,
    role:
      customer?.userRole === 6
        ? { name: "Client", code: "Client" }
        : customer?.userRole === 5
          ? { name: "Prospect", code: "Prospect" }
          : { name: defaultRole, code: defaultRole },
    password: "",
  });
  const [disabled, setDisabled] = useState(true);
  const [customErrors, setCustomErrors] = useState({});
  // "email" | "sms" while an invitation send is in flight.
  const [sendingInvitation, setSendingInvitation] = useState(null);

  const handleSendInvitation = async (channel) => {
    if (sendingInvitation) return;
    setSendingInvitation(channel);
    try {
      await NEW_API.post("/api/magic-login/send-invitation", {
        userId: customer?.id,
        channel,
      });
      toast.success(t("Invitation sent!"));
    } catch (error) {
      console.log(error);
      const code =
        error?.response?.data?.error?.message ??
        error?.response?.data?.error?.name;
      if (code === "SMS_NOT_CONFIGURED") {
        toast.error(t("SMS sending is not configured yet"));
      } else if (code === "INVITATION_TEMPLATE_NOT_CONFIGURED") {
        toast.error(t("The invitation email template is not configured"));
      } else if (code === "NO_PHONE") {
        toast.error(t("This customer has no phone number"));
      } else if (code === "NO_EMAIL") {
        toast.error(t("This customer has no e-mail address"));
      } else if (code === "SEND_FAILED") {
        toast.error(t("The invitation could not be sent"));
      } else {
        toast.error(t("An error has occurred!"));
      }
    } finally {
      setSendingInvitation(null);
    }
  };

  const abortRef = useRef(null);
  useEffect(() => {
    abortRef.current = new AbortController();
    // Countries WITHOUT their cities — populating every city of every country
    // is what made this modal slow. Cities load per selected country below.
    dispatch(getCountries(false, abortRef.current.signal));
    return () => abortRef.current?.abort();
  }, [dispatch]);

  // Same deep-equality issue as the city: swap the prefilled store for the
  // loaded option so the Select shows it.
  useEffect(() => {
    if (!formData.organization?.code || !organizationOptions.length) return;
    const match = organizationOptions.find(
      (o) => o.code === formData.organization.code
    );
    if (match && match !== formData.organization) {
      setFormData((prev) => ({ ...prev, organization: match }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationOptions]);

  // Fetch the selected country's cities only (also covers the edit prefill).
  const citiesAbortRef = useRef(null);
  useEffect(() => {
    if (!formData.country?.code) return;
    citiesAbortRef.current?.abort();
    citiesAbortRef.current = new AbortController();
    dispatch(
      getCountriesCitiesNew(
        formData.country.code,
        "",
        100000,
        citiesAbortRef.current.signal,
      ),
    );
    return () => citiesAbortRef.current?.abort();
  }, [dispatch, formData.country?.code]);

  // The Dropdown matches the selected VALUE to an option by deep equality —
  // the {name, code} prefill never equals the loaded option (which also has
  // cityCode/zipcode), so swap it for the real option once cities arrive.
  useEffect(() => {
    if (!formData.city?.code || !cities?.length) return;
    const match = cities.find((c) => c.code === formData.city.code);
    if (match && match !== formData.city) {
      setFormData((prev) => ({ ...prev, city: match }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities]);

  const roleOptions = [
    { name: t("Prospect"), code: "Prospect" },
    { name: t("Client"), code: "Client" },
  ];

  const cityOptions = formData.country ? (cities ?? []) : [];

  // Contact = prospect source; legacy free-text values stay selectable.
  const contactOptions = prospectSourceOptions(t);
  if (
    customer?.contact &&
    !contactOptions.some((option) => option.code === customer.contact)
  ) {
    contactOptions.unshift({ name: customer.contact, code: customer.contact });
  }

  const selectOptions = {
    role: roleOptions,
    country: countries ?? [],
    city: cityOptions,
    contact: contactOptions,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
      // The city list depends on the country — reset it on country change.
      ...(name === "country" ? { city: null } : {}),
    }));
  };

  const checkFormValidation = useCallback(() => {
    const invalidFields = [];
    formElements.forEach(({ name, required, type }) => {
      if (!required) return;
      if (type === "select") {
        if (!formData[name]?.code) invalidFields.push(name);
      } else if (!String(formData[name] ?? "").trim()) {
        invalidFields.push(name);
      }
    });
    // Password is required at creation only (min 6, enforced by the backend).
    if (!isEdit && !formData.password) invalidFields.push("password");
    // A business affiliate has no store of its own: it must pick one.
    if (isAffiliate && !formData.organization?.code) {
      invalidFields.push("organization");
    }
    setDisabled(invalidFields.length > 0);
  }, [formData, isEdit, isAffiliate]);

  useEffect(() => {
    checkFormValidation();
  }, [checkFormValidation]);

  const handleSave = () => {
    if (!emailPattern.test(formData.email)) {
      setCustomErrors({ email: t("Please enter a valid email.") });
      setActiveTab(TABS[0]);
      return;
    }
    setCustomErrors({});

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim() || undefined,
      zipcode: formData.zipcode.trim() || undefined,
      contact: formData.contact?.code || undefined,
      externalNote: formData.externalNote.trim() || undefined,
      markers: formData.markers,
      role: formData.role?.code,
      ...(canPickOrganization && formData.organization?.code
        ? { organizationId: formData.organization.code }
        : {}),
      address: {
        street: formData.street.trim() || undefined,
        zip: formData.zipcode.trim() || undefined,
        city: formData.city?.code,
        country: formData.country?.code,
      },
      ...(formData.password ? { password: formData.password } : {}),
    };

    if (isEdit) {
      dispatch(
        updateAppCustomer(customer.id, payload, t, () =>
          dispatch(closeRightModal()),
        ),
      );
    } else {
      dispatch(
        createAppCustomer(payload, t, () => dispatch(closeRightModal())),
      );
    }
  };

  return (
    <div className="p-4 d-flex flex-column h-100">
      <h2 className="rightModalTitle quickSand bold text-blue mb-4">
        {isEdit ? t("Modify a customer") : t("Add a customer")}
      </h2>
      <TabsNavbar
        tabs={TABS}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {activeTab === "General" ? (
        <div className="row">
          {formElements.map((el) => (
            <div className="col-6 mt-4" key={el?.name}>
              <p className="f16 text-ultraBlack mb-2">
                {t(`${el?.label}`)}
                {el?.required ? <span className="text-red"> *</span> : null}
              </p>
              {el.type === "select" ? (
                <Select
                  {...el}
                  value={formData[el?.name]}
                  options={selectOptions[el?.name] ?? []}
                  callback={handleChange}
                  filter={el.name !== "role"}
                  showClear={el.name !== "role"}
                  defaultValue={formData[el?.name]}
                  // Web-origin sources are set automatically and stay locked.
                  disabled={
                    el.name === "contact" &&
                    AUTO_SOURCES.includes(customer?.contact)
                  }
                  appendToSelf
                />
              ) : (
                <TextInput
                  {...el}
                  value={formData[el?.name]}
                  callback={handleChange}
                />
              )}
              {customErrors[el?.name] ? (
                <p className="f12 text-red mt-1">{customErrors[el?.name]}</p>
              ) : null}
            </div>
          ))}
          {canPickOrganization ? (
            <div className="col-6 mt-4">
              <p className="f16 text-ultraBlack mb-2">
                {t("Reseller")}
                {isAffiliate ? <span className="text-red"> *</span> : null}
              </p>
              <Select
                name="organization"
                value={formData.organization}
                options={organizationOptions}
                callback={handleChange}
                filter
                showClear
                appendToSelf
              />
            </div>
          ) : null}
          <div className="col-6 mt-4">
            <p className="f16 text-ultraBlack mb-2">
              {t("Password")}
              {!isEdit ? <span className="text-red"> *</span> : null}
            </p>
            <TextInput
              type="password"
              name="password"
              value={formData.password}
              callback={handleChange}
              placeholder={isEdit ? t("Leave empty to keep unchanged") : ""}
            />
          </div>
          {isEdit ? (
            <div className="col-6 mt-4">
              <p className="f16 text-ultraBlack mb-2">
                {t("Application account number")}
              </p>
              <TextInput value={customer?.id ?? ""} disabled />
            </div>
          ) : null}
        </div>
      ) : activeTab === "External note" ? (
        <div className="row">
          <div className="col-12 mt-4">
            <p className="f16 text-ultraBlack mb-2">{t("External note")}</p>
            <TextArea
              name="externalNote"
              rows={10}
              value={formData.externalNote}
              callback={handleChange}
            />
          </div>
        </div>
      ) : (
        // Same map editor as the prospect form's Location tab (geocoder +
        // draggable marker), limited to ONE point — a customer has a single
        // location. It reports { markers: [{coordinates}] }; the "geoEntity"
        // name arg is ignored — customers store markers as json.
        // 650px: the shared .map class has a 600px min-height + own padding.
        <div style={{ height: 650 }} className="mt-3">
          <Location
            singleMarker
            initialValues={{ markers: formData.markers }}
            handleFormChange={(_, data) =>
              setFormData((prev) => ({ ...prev, markers: data?.markers ?? [] }))
            }
          />
        </div>
      )}
      {isEdit ? (
        <div className="d-flex gap-4 mt-5">
          <Button
            loading={sendingInvitation === "email"}
            style={{ backgroundColor: "#DB8B4D", borderColor: "#DB8B4D" }}
            onClick={() => handleSendInvitation("email")}
          >
            {t("Send QR Code invitation")}
          </Button>
          <Button
            loading={sendingInvitation === "sms"}
            style={{ backgroundColor: "#9BC8CD", borderColor: "#9BC8CD" }}
            onClick={() => handleSendInvitation("sms")}
          >
            {t("Send SMS invitation")}
          </Button>
        </div>
      ) : null}
      <div className="d-flex justify-content-end mt-4">
        <Button
          variant="outline"
          className="me-4"
          onClick={() => dispatch(closeRightModal())}
        >
          {t("Cancel")}
        </Button>
        <Button
          loading={createLoading || updateLoading}
          disabled={disabled}
          onClick={createLoading || updateLoading ? undefined : handleSave}
        >
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};

export default AppCustomerModal;
